import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CastRes from "types/res/cast/CastRes";
import CastApi from "api/CastApi";
import LineApi, {
  CastLineCompanyStatus,
  IssueLinkKeyResponse,
} from "api/LineApi";
import {
  Box,
  Button,
  CircularProgress,
  createStyles,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { changeAccount } from "redux/actions/account";
import { useHistory } from "react-router-dom";
import { Alert } from "@material-ui/lab";
// @ts-ignore — qrcode.react v1.x の型は別パッケージで未提供のため
import QRCode from "qrcode.react";

const POLLING_INTERVAL_MS = 3000;
const POLLING_MAX_DURATION_MS = 5 * 60 * 1000;

const useStyles = makeStyles(() =>
  createStyles({
    section: { padding: 16, marginTop: 16 },
    companyCard: { padding: 16, marginTop: 12 },
    keyText: {
      fontSize: 32,
      fontWeight: "bold",
      letterSpacing: 6,
      fontFamily: "monospace",
      textAlign: "center",
    },
    qrBox: {
      display: "flex",
      justifyContent: "center",
      padding: 16,
      backgroundColor: "#fff",
    },
    button: {},
  })
);

type ActiveLinkKey = {
  companyId: number;
  data: IssueLinkKeyResponse;
};

const Setting: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const company = useSelector((state: any) => state.account.cast.company);
  const email = useSelector((state: any) => state.account.cast.email);
  const classes = useStyles();

  const [castList, setCastList] = useState<CastRes[]>([]);
  const [statusList, setStatusList] = useState<CastLineCompanyStatus[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [issuingCompanyId, setIssuingCompanyId] = useState<number | null>(null);
  const [activeKey, setActiveKey] = useState<ActiveLinkKey | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const pollingStartedAtRef = useRef<number>(0);

  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(max-width: 768px)").matches;

  const fetchStatusAll = useCallback(async (): Promise<
    CastLineCompanyStatus[] | null
  > => {
    try {
      const result = await LineApi.getStatusAll();
      setStatusList(result || []);
      return result || [];
    } catch (e) {
      console.error("LINE statusAll fetch failed:", e);
      return null;
    }
  }, []);

  // 所属企業一覧 (CastRes)
  useEffect(() => {
    const apiCall = async () => {
      const result = await CastApi.findByEmail(email);
      setCastList(result);
    };
    apiCall();
  }, [email]);

  // 初回 statusAll 取得
  useEffect(() => {
    setLoadingStatus(true);
    fetchStatusAll().finally(() => setLoadingStatus(false));
  }, [fetchStatusAll]);

  // ポーリング: activeKey が出ている間は連携完了を監視する
  useEffect(() => {
    const stopPolling = () => {
      if (pollingTimerRef.current !== null) {
        window.clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
    if (!activeKey) {
      stopPolling();
      return;
    }
    pollingStartedAtRef.current = Date.now();
    const tick = async () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      if (Date.now() - pollingStartedAtRef.current > POLLING_MAX_DURATION_MS) {
        stopPolling();
        return;
      }
      const result = await fetchStatusAll();
      const target = result?.find((s) => s.companyId === activeKey.companyId);
      if (target?.isLinked) {
        stopPolling();
        setActiveKey(null);
      }
    };
    pollingTimerRef.current = window.setInterval(tick, POLLING_INTERVAL_MS);
    return stopPolling;
  }, [activeKey, fetchStatusAll]);

  // カウントダウン
  useEffect(() => {
    if (!activeKey) {
      setRemainingMs(0);
      return;
    }
    const expiresAt = new Date(activeKey.data.expiresAt).getTime();
    const update = () => {
      const remaining = expiresAt - Date.now();
      setRemainingMs(remaining > 0 ? remaining : 0);
      if (remaining <= 0) {
        setActiveKey(null);
      }
    };
    update();
    const interval = window.setInterval(update, 1000);
    return () => window.clearInterval(interval);
  }, [activeKey]);

  const onClickChangeAccount = (cast: CastRes) => {
    dispatch(changeAccount({ cast }));
  };

  const handleIssueLinkKey = async (companyId: number) => {
    setIssuingCompanyId(companyId);
    try {
      const result = await LineApi.issueLinkKey(companyId);
      setActiveKey({ companyId, data: result });
    } catch (e: any) {
      alert(e?.message || "連携キーの発行に失敗しました");
    }
    setIssuingCompanyId(null);
  };

  const handleCancelKey = () => {
    setActiveKey(null);
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage(`${label}をコピーしました`);
      setTimeout(() => setCopyMessage(null), 1500);
    } catch (e) {
      setCopyMessage("コピーに失敗しました");
    }
  };

  const handleUnlink = async (companyId: number, companyName: string) => {
    if (!window.confirm(`${companyName} のLINE連携を解除しますか？`)) return;
    try {
      await LineApi.unlink(companyId);
      await fetchStatusAll();
    } catch (e) {
      alert("連携解除に失敗しました");
    }
  };

  const handleNotifyMethodChange = async (
    companyId: number,
    next: "email" | "line" | "both"
  ) => {
    try {
      await LineApi.updateNotifyMethod(companyId, next);
      await fetchStatusAll();
    } catch (e) {
      alert("通知方式の保存に失敗しました");
    }
  };

  const renderCompanyCard = (status: CastLineCompanyStatus) => {
    const isCurrentlyShowingKey = activeKey?.companyId === status.companyId;

    return (
      <Paper
        key={status.companyId}
        variant="outlined"
        className={classes.companyCard}
      >
        <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
          🏢 {status.companyName}
        </Typography>

        {/* 連携キー発行中 */}
        {isCurrentlyShowingKey && activeKey && (
          <Box marginTop={2}>
            <Typography variant="body2" gutterBottom>
              以下の手順で連携を完了してください:
            </Typography>
            <Typography variant="body2" component="div">
              <ol style={{ paddingLeft: 18 }}>
                <li>
                  「{status.companyName}」の LINE 公式アカウントを友だち追加
                </li>
                <li>LINE のトーク画面で 6桁の連携キーを送信</li>
                <li>このページに自動で反映されます</li>
              </ol>
            </Typography>

            <Paper variant="outlined" style={{ padding: 16, marginTop: 8 }}>
              <Typography variant="caption" color="textSecondary">
                連携キー
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="center">
                <Typography className={classes.keyText}>
                  {activeKey.data.linkKey}
                </Typography>
                <Tooltip title="コピー">
                  <IconButton
                    onClick={() =>
                      handleCopy(activeKey.data.linkKey, "連携キー")
                    }
                    size="small"
                  >
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", textAlign: "center" }}
              >
                残り時間 {Math.floor(Math.ceil(remainingMs / 1000) / 60)}:
                {String(Math.ceil(remainingMs / 1000) % 60).padStart(2, "0")}
              </Typography>
            </Paper>

            <Box marginTop={2}>
              {isMobile ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  href={activeKey.data.addFriendUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LINEで友だち追加
                </Button>
              ) : (
                <Box>
                  <Typography variant="body2" align="center">
                    スマホで下記の QR コードを読み取って友だち追加してください
                  </Typography>
                  <Box className={classes.qrBox}>
                    <QRCode value={activeKey.data.addFriendUrl} size={180} />
                  </Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    align="center"
                    display="block"
                  >
                    ベーシックID: {activeKey.data.botBasicId}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box marginTop={2} display="flex" justifyContent="space-between">
              <Button onClick={handleCancelKey}>キャンセル</Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => fetchStatusAll()}
              >
                連携状況を再確認
              </Button>
            </Box>

            {copyMessage && (
              <Typography
                variant="caption"
                style={{ color: "green", display: "block", marginTop: 8 }}
              >
                {copyMessage}
              </Typography>
            )}
          </Box>
        )}

        {/* キー発行中でない時の通常表示 */}
        {!isCurrentlyShowingKey && (
          <Box marginTop={1}>
            {!status.isStoreReady ? (
              <Typography variant="body2" style={{ color: "#999" }}>
                ⚠️ 店舗側の LINE 設定が未完了です。店舗管理者にお問い合わせください。
              </Typography>
            ) : status.isLinked ? (
              <Box>
                <Typography variant="body2" style={{ color: "green" }}>
                  ✅ 連携済み
                  {status.lineLinkedAt && (
                    <span
                      style={{ marginLeft: 8, color: "#666", fontSize: 12 }}
                    >
                      ({new Date(status.lineLinkedAt).toLocaleDateString()})
                    </span>
                  )}
                </Typography>
                <Box marginTop={1} display="flex" alignItems="center">
                  <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                    <InputLabel>通知方式</InputLabel>
                    <Select
                      value={status.notifyMethod}
                      onChange={(e) =>
                        handleNotifyMethodChange(
                          status.companyId,
                          e.target.value as "email" | "line" | "both"
                        )
                      }
                      label="通知方式"
                    >
                      <MenuItem value="email">メールのみ</MenuItem>
                      <MenuItem value="line">LINEのみ</MenuItem>
                      <MenuItem value="both">メール+LINE</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    style={{ marginLeft: 8 }}
                    onClick={() =>
                      handleUnlink(status.companyId, status.companyName)
                    }
                  >
                    解除
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  ❌ 未連携
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleIssueLinkKey(status.companyId)}
                  disabled={issuingCompanyId === status.companyId}
                >
                  {issuingCompanyId === status.companyId
                    ? "発行中..."
                    : "LINE連携する"}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box display="flex" flexDirection="column">
      <Box margin={2}>
        <Typography>現在のログイン中企業：{company.name}</Typography>
      </Box>
      <Divider />
      <Box display="flex" flexDirection="column" margin={2}>
        <Typography variant={"subtitle1"}>所属企業の切り替え</Typography>
        {castList.map((cast, idx) => (
          <Box
            key={idx}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            marginY={1}
          >
            <Typography>{cast.company.name}</Typography>
            <Button
              variant="contained"
              className={classes.button}
              color="secondary"
              size="small"
              onClick={() => onClickChangeAccount(cast)}
            >
              切り替え
            </Button>
          </Box>
        ))}
      </Box>
      <Divider />

      <Box margin={2}>
        <Typography variant="subtitle1">LINE 連携</Typography>
        <Typography variant="caption" color="textSecondary">
          複数企業に所属している場合は、それぞれの企業ごとに連携が必要です。
        </Typography>
        {loadingStatus ? (
          <Box display="flex" justifyContent="center" padding={2}>
            <CircularProgress size={24} />
          </Box>
        ) : statusList.length === 0 ? (
          <Paper className={classes.section}>
            <Typography variant="body2" color="textSecondary">
              所属企業が見つかりませんでした。
            </Typography>
          </Paper>
        ) : (
          statusList.map(renderCompanyCard)
        )}
      </Box>

      <Divider />
      <Box marginTop={2} display={"flex"} justifyContent={"center"}>
        <Alert severity={"error"}>
          企業が追加された方は一度ログアウトし、パスワードリセットを行ってください
        </Alert>
      </Box>
      <Box marginTop={2} display={"flex"} justifyContent={"center"}>
        <Button
          variant="contained"
          className={classes.button}
          color="default"
          onClick={() => history.push("/")}
        >
          トップページへ
        </Button>
      </Box>
    </Box>
  );
};

export default Setting;
